import {
  Condition,
  NumOrdersOption,
} from "@redotech/redo-model/return-flow/condition";
import * as React from "react";
import { ConditionType, ConditionTypeDetailsProps } from "../condition";
import {
  DateSubconditionState,
  emptyDateSubconditionState,
  fromModelDateSubcondition,
  getDateSubconditionSuffix,
  toModelDateSubcondition,
  validateDateSubcondition,
} from "./date-subcondition";

export interface NumOrdersState {
  numOrders: string;
  orderOption: NumOrdersOption | null;
  dateSubcondition: DateSubconditionState;
}

export const NUM_ORDERS = (
  Details: React.ComponentType<ConditionTypeDetailsProps<NumOrdersState>>,
): ConditionType<NumOrdersState, Condition.NumOrders> => ({
  name: "Number of orders",
  description(state) {
    if (!state.orderOption) {
      return "The total number of orders this customer has placed";
    }

    let description = `Number of orders ${state.orderOption.toLowerCase()} ${state.numOrders}`;

    // Add date subcondition suffix from shared helper
    description += getDateSubconditionSuffix(state.dateSubcondition);

    return description;
  },
  Details(props: ConditionTypeDetailsProps<NumOrdersState>) {
    return React.createElement(Details, props);
  },
  empty: {
    numOrders: (0).toFixed(0),
    orderOption: null,
    dateSubcondition: emptyDateSubconditionState,
  },
  fromModel(model) {
    return {
      numOrders: model.numOrders.toFixed(0),
      orderOption: model.numOrdersOption,
      // Convert dateSubcondition or migrate from legacy fields
      dateSubcondition: model.dateSubcondition
        ? fromModelDateSubcondition(model.dateSubcondition)
        : emptyDateSubconditionState,
    };
  },
  toModel(state) {
    return {
      type: Condition.NUM_ORDERS,
      numOrders: +state.numOrders,
      numOrdersOption: state.orderOption,
      // Use only dateSubcondition going forward
      dateSubcondition: toModelDateSubcondition(state.dateSubcondition),
    };
  },
  valid(state) {
    const orderConditionValid =
      state.orderOption !== null &&
      +state.numOrders >= 0 &&
      +state.numOrders % 1 === 0; // Must be whole number

    // Validate date subcondition
    const dateSubconditionValid = validateDateSubcondition(
      state.dateSubcondition,
    );

    return orderConditionValid && dateSubconditionValid;
  },
});
